import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

/**
 * Interface para representar um valor de campo com metadata
 */
export interface FieldValue {
  value: any;
  timestamp: number;
  source: 'grid' | 'form' | 'selection' | 'initial';
}

/**
 * FormSyncService - Serviço de Sincronização de Formulários
 * 
 * Responsável por comunicação reativa entre formulários e grids,
 * eliminando a necessidade de manipulação direta do DOM.
 * 
 * Fluxo:
 * 1. Grid/Selection atualiza valores via setFieldValues()
 * 2. Componentes de campo subscrevem via getFieldValue$()
 * 3. Quando campo muda, notifica via notifyFieldChange()
 * 
 * IMPORTANTE: Este serviço NÃO é global (providedIn: 'root')
 * Ele deve ser fornecido a nível de componente para garantir
 * instâncias isoladas por launcher.
 */
@Injectable()
export class FormSyncService {
  
  /** Map de valores de campos: fieldId -> FieldValue */
  private fieldValuesSubject = new BehaviorSubject<Map<string, FieldValue>>(new Map());
  
  /** Observable público dos valores dos campos */
  fieldValues$ = this.fieldValuesSubject.asObservable();
  
  /** Subject para notificar mudanças de campo (do form para o grid) */
  private fieldChangeSubject = new Subject<{
    fieldId: string;
    dataPath: string;
    value: any;
    panelId?: string;
  }>();
  
  /** Observable para mudanças de campo */
  fieldChange$ = this.fieldChangeSubject.asObservable();
  
  /** Contexto atual (panel IDs que devem receber atualizações) */
  private activePanelIds: Set<string> = new Set();
  
  /** Timestamp da última atualização em lote */
  private lastBatchUpdateTime = 0;
  
  /** Janela para suprimir atualizações duplicadas (ms) */
  private static readonly SUPPRESS_DUPLICATE_MS = 100;
  
  constructor() {}
  
  /**
   * Define valores de múltiplos campos de uma vez
   * @param values Map de fieldId -> valor
   * @param source Origem da atualização
   * @param panelIds IDs dos painéis que devem ser atualizados (opcional)
   */
  setFieldValues(
    values: Map<string, any>,
    source: 'grid' | 'form' | 'selection' | 'initial' = 'selection',
    panelIds?: string[]
  ): void {
    const now = Date.now();
    const current = this.fieldValuesSubject.value;
    const updated = new Map(current);
    
    // Atualizar contexto de painéis ativos
    if (panelIds) {
      this.activePanelIds = new Set(panelIds);
    }
    
    values.forEach((value, fieldId) => {
      updated.set(fieldId, {
        value,
        timestamp: now,
        source
      });
    });
    
    this.lastBatchUpdateTime = now;
    this.fieldValuesSubject.next(updated);
  }
  
  /**
   * Atualiza valor de um único campo
   * @param fieldId ID do campo
   * @param value Novo valor
   * @param source Origem da atualização
   */
  updateFieldValue(
    fieldId: string,
    value: any,
    source: 'grid' | 'form' | 'selection' | 'initial' = 'form'
  ): void {
    const now = Date.now();
    const current = this.fieldValuesSubject.value;
    const existing = current.get(fieldId);
    
    // Suprimir atualizações duplicadas em curto intervalo
    if (existing && 
        existing.value === value && 
        (now - existing.timestamp) < FormSyncService.SUPPRESS_DUPLICATE_MS) {
      return;
    }
    
    const updated = new Map(current);
    updated.set(fieldId, {
      value,
      timestamp: now,
      source
    });
    
    this.fieldValuesSubject.next(updated);
  }
  
  /**
   * Obtém Observable do valor de um campo específico
   * @param fieldId ID do campo
   * @returns Observable que emite quando o valor muda
   */
  getFieldValue$(fieldId: string): Observable<any> {
    return this.fieldValues$.pipe(
      map(values => values.get(fieldId)?.value),
      distinctUntilChanged()
    );
  }
  
  /**
   * Obtém Observable completo de um campo (com metadata)
   * @param fieldId ID do campo
   * @returns Observable que emite FieldValue completo
   */
  getFieldValueWithMeta$(fieldId: string): Observable<FieldValue | undefined> {
    return this.fieldValues$.pipe(
      map(values => values.get(fieldId)),
      distinctUntilChanged((a, b) => 
        a?.value === b?.value && a?.timestamp === b?.timestamp
      )
    );
  }
  
  /**
   * Obtém valor atual de um campo (síncrono)
   * @param fieldId ID do campo
   * @returns Valor atual ou undefined
   */
  getFieldValue(fieldId: string): any {
    return this.fieldValuesSubject.value.get(fieldId)?.value;
  }
  
  /**
   * Notifica que um campo foi alterado (do formulário para o grid)
   * @param fieldId ID do campo
   * @param dataPath DataPath do campo (para atualizar o JSON)
   * @param value Novo valor
   * @param panelId ID do painel onde o campo está (opcional)
   */
  notifyFieldChange(fieldId: string, dataPath: string, value: any, panelId?: string): void {
    // Atualizar valor local primeiro
    this.updateFieldValue(fieldId, value, 'form');
    
    // Emitir evento de mudança
    this.fieldChangeSubject.next({
      fieldId,
      dataPath,
      value,
      panelId
    });
  }
  
  /**
   * Verifica se um painel está ativo para receber atualizações
   * @param panelId ID do painel
   */
  isPanelActive(panelId: string): boolean {
    return this.activePanelIds.size === 0 || this.activePanelIds.has(panelId);
  }
  
  /**
   * Limpa valores de todos os campos
   */
  clearAllFields(): void {
    this.fieldValuesSubject.next(new Map());
    this.activePanelIds.clear();
  }
  
  /**
   * Limpa valores de campos de um painel específico
   * @param panelId ID do painel
   * @param fieldIds Lista de IDs de campos a limpar
   */
  clearPanelFields(panelId: string, fieldIds: string[]): void {
    const current = this.fieldValuesSubject.value;
    const updated = new Map(current);
    
    fieldIds.forEach(fieldId => {
      updated.delete(fieldId);
    });
    
    this.fieldValuesSubject.next(updated);
  }
  
  /**
   * Verifica se a última atualização foi recente (para evitar loops)
   */
  isRecentUpdate(): boolean {
    return (Date.now() - this.lastBatchUpdateTime) < FormSyncService.SUPPRESS_DUPLICATE_MS;
  }
}
